// Meta Pixel bridge. The pixel snippet itself is injected by
// DeferredAnalyticsScripts only after tracking consent, so every call here is a
// no-op until then — `fbq` simply does not exist yet.

import { consumeFbEventId } from './fbEventId'
import { whenPixelReady } from './whenPixelReady'

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
  }
}

/**
 * Meta's standard conversion event for a completed signup.
 *
 * The same conversion also arrives from the Conversions API, so both carry the
 * id minted for this signup — Meta collapses them into one. A missing id (an
 * older client, or storage unavailable) still reports the conversion; it just
 * loses deduplication against the server event.
 */
export const trackMetaCompleteRegistration = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  whenPixelReady(
    () => typeof window.fbq === 'function',
    () => {
      // Read the id only once fbq exists. consumeFbEventId clears it, so doing
      // it up front would burn the id on a call that turns out to be a no-op
      // and leave the conversion undeduplicable — reported by the server alone.
      const eventID = consumeFbEventId()

      try {
        if (eventID) {
          window.fbq?.('track', 'CompleteRegistration', {}, { eventID })
        } else {
          window.fbq?.('track', 'CompleteRegistration')
        }
      } catch {
        // best-effort, mirrors pushDataLayerEvent
      }
    }
  )
}
